import { ApiError } from '@/platform/lib/api';
import { SAAS_COMMUNITIES_PATH } from '@/platform/saas/saasHostUi';
import { saasStartPath } from '@/platform/saas/tenantSite';
import { syncProfile, type UserProfile } from '@/platform/users/api';

export const JOIN_GATE_INVITE = 'invite_required';
export const JOIN_GATE_APPROVAL = 'join_approval_required';

export type JoinGateCode = typeof JOIN_GATE_INVITE | typeof JOIN_GATE_APPROVAL;

export const JOIN_GATE_COPY: Record<JoinGateCode, { title: string; body: string }> = {
  invite_required: {
    title: 'Invite only',
    body: 'This community is invite-only. Ask the host for an invite link.',
  },
  join_approval_required: {
    title: 'Approval required',
    body: 'Membership requires host approval. Request to join from the community start page or wait for approval.',
  },
};

export class JoinGateError extends Error {
  readonly code: JoinGateCode;

  constructor(code: JoinGateCode, message?: string) {
    super(message?.trim() || JOIN_GATE_COPY[code].body);
    this.name = 'JoinGateError';
    this.code = code;
  }
}

export function isJoinGateCode(code: string | undefined): code is JoinGateCode {
  return code === JOIN_GATE_INVITE || code === JOIN_GATE_APPROVAL;
}

export function joinGateCopy(code: JoinGateCode): { title: string; body: string } {
  return JOIN_GATE_COPY[code];
}

export function joinGateFromUnknown(err: unknown): JoinGateError | null {
  if (err instanceof JoinGateError) return err;
  if (err instanceof ApiError && isJoinGateCode(err.code)) {
    return new JoinGateError(err.code, err.message);
  }
  return null;
}

/** First label of a tenant host (`slug.example.com`). Apex / ops / www → empty. */
export function tenantSlugFromHostname(hostname: string): string {
  const host = hostname.toLowerCase().split(':')[0];
  const parts = host.split('.').filter(Boolean);
  if (parts.length < 3) return '';
  const sub = parts[0];
  if (!sub || sub === 'www' || sub === 'ops' || sub === 'app') return '';
  return /^[a-z0-9-]{2,48}$/.test(sub) ? sub : '';
}

export function joinRequestHref(hostname: string): string {
  return saasStartPath(tenantSlugFromHostname(hostname));
}

/**
 * Paths that stay reachable when the user is signed in but not a community member.
 * Public marketplace browse/buy is open; sell stays gated.
 */
const JOIN_GATE_PASSTHROUGH_PREFIXES = [
  '/legal/',
  '/login',
  saasStartPath('').replace(/\/$/, ''),
  SAAS_COMMUNITIES_PATH.replace(/\/$/, ''),
  '/marketplace/checkout/',
  '/marketplace/orders/',
  '/marketplace/sellers/',
  '/marketplace/hauls/',
] as const;

/** True when join-gate UI must not block this pathname. */
export function isJoinGatePassthrough(pathname: string): boolean {
  const path = pathname || '/';
  if (JOIN_GATE_PASSTHROUGH_PREFIXES.some((p) => path.startsWith(p))) return true;
  // Directory root and trailing-slash variants — not /marketplace/sell/
  if (path === '/marketplace' || path === '/marketplace/') return true;
  return false;
}

/** Profile sync that maps invite/approval 403s to JoinGateError. */
export async function syncProfileWithJoinGate(): Promise<UserProfile> {
  try {
    return await syncProfile();
  } catch (err) {
    const gate = joinGateFromUnknown(err);
    if (gate) throw gate;
    throw err;
  }
}
